import React, { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { outboundUrl, trackOutboundClick } from "../lib/outbound";
import { ExternalLink, Plus, Check } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const CATEGORIES = ["All", "Recovery", "Comfort", "Self Care", "Entertainment", "Nutrition"];

export default function Browse() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get("category") || "All";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addedIds, setAddedIds] = useState(new Set());

  const load = async (cat) => {
    setLoading(true);
    try {
      const q = cat && cat !== "All" ? `?category=${encodeURIComponent(cat)}` : "";
      const { data } = await api.get(`/curated${q}`);
      setItems(data);
    } catch (e) {
      toast.error("Could not load curated items.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(activeCategory);
  }, [activeCategory]);

  const addToRegistry = async (ci) => {
    if (!user || typeof user !== "object") {
      toast("Sign in to add to your registry.", {
        action: { label: "Sign in", onClick: () => navigate("/login") },
      });
      return;
    }
    try {
      await api.post("/registry/me/items", {
        product_name: ci.product_name,
        url: ci.url || ci.base_url,
        image_url: ci.image_url,
        price: ci.price,
        category: ci.category,
        notes: ci.description || "",
      });
      setAddedIds(new Set([...addedIds, ci.id]));
      toast.success("Added to your registry.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not add.");
    }
  };

  const setCat = (c) => {
    if (c === "All") setSearchParams({});
    else setSearchParams({ category: c });
  };

  return (
    <div className="container-x py-12 lg:py-16" data-testid="browse-page">
      <div className="max-w-2xl">
        <span className="tag-pill">Curated for recovery</span>
        <h1 className="mt-3 font-outfit text-4xl lg:text-5xl font-medium text-ink tracking-tight">
          Browse curated items
        </h1>
        <p className="mt-4 font-manrope text-ink-soft leading-relaxed">
          Thoughtfully chosen for hysterectomy recovery. One-click add to your registry — or use
          them as a starting list to share with a friend.
        </p>
      </div>

      <div className="mt-10 flex flex-wrap gap-2" data-testid="browse-categories">
        {CATEGORIES.map((c) => (
          <button
            key={c}
            onClick={() => setCat(c)}
            className={`rounded-full px-4 py-2 text-sm font-medium transition-colors border ${
              activeCategory === c
                ? "bg-brand text-white border-brand"
                : "bg-white text-ink-soft border-[#EBE2E8] hover:border-accent2"
            }`}
            data-testid={`cat-btn-${c}`}
          >
            {c}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="mt-10 font-manrope text-ink-soft animate-soft-pulse">Loading curated goods…</p>
      ) : (
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {items.map((ci) => {
            const added = addedIds.has(ci.id);
            return (
              <div key={ci.id} className="card-soft card-soft-hover p-4 flex flex-col" data-testid={`curated-${ci.id}`}>
                <div className="w-full aspect-[4/3] rounded-xl overflow-hidden bg-cream-surface">
                  {ci.image_url ? (
                    <img src={ci.image_url} alt={ci.product_name} className="w-full h-full object-cover" />
                  ) : null}
                </div>
                <div className="mt-3 flex-1">
                  <span className="tag-pill text-xs">{ci.category}</span>
                  <h3 className="mt-2 font-outfit text-base font-medium text-ink line-clamp-2">
                    {ci.product_name}
                  </h3>
                  {ci.description && (
                    <p className="mt-1 font-manrope text-xs text-ink-soft line-clamp-2">{ci.description}</p>
                  )}
                  {ci.price != null && (
                    <p className="mt-2 font-manrope text-sm text-ink-soft">${ci.price.toFixed(2)}</p>
                  )}
                </div>
                <div className="mt-4 flex items-center justify-between gap-2">
                  <a
                    href={outboundUrl(ci)}
                    target="_blank"
                    rel="noreferrer sponsored"
                    onClick={() => trackOutboundClick({ kind: "curated", itemId: ci.id, destination: outboundUrl(ci) })}
                    className="text-sm text-brand font-medium inline-flex items-center gap-1 hover:underline"
                    data-testid={`curated-view-${ci.id}`}
                  >
                    View <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                  <button
                    onClick={() => addToRegistry(ci)}
                    disabled={added}
                    className={added ? "btn-ghost text-sm inline-flex items-center gap-1" : "btn-primary text-sm inline-flex items-center gap-1"}
                    data-testid={`curated-add-${ci.id}`}
                  >
                    {added ? (<><Check className="h-4 w-4" /> Added</>) : (<><Plus className="h-4 w-4" /> Add</>)}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!user && (
        <div className="mt-14 card-soft p-8 text-center" data-testid="browse-signin-cta">
          <p className="font-manrope text-ink-soft">
            <Link to="/login" className="text-brand font-medium hover:underline">Sign in</Link> or{" "}
            <Link to="/register" className="text-brand font-medium hover:underline">create an account</Link>{" "}
            to add items to your registry.
          </p>
        </div>
      )}
    </div>
  );
}
